/* eslint-disable @typescript-eslint/no-explicit-any */
import { BrowserModule } from '@angular/platform-browser';
import { APP_ID, APP_INITIALIZER, NgModule } from '@angular/core';

// import ngx-translate and the http loader
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withFetch, withInterceptorsFromDi } from '@angular/common/http';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { SharedModule } from './shared';
import { AuthModule } from './auth/auth.module';
import { CoreModule } from './core/core.module';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ToastrModule } from 'ngx-toastr';
import { InitService } from './core/services/init.service';
import { AuthService } from './core/services/auth.service';
import { HttpTokenInterceptor } from './core/interceptors/http.token.interceptor';
import { HttpErrorInterceptor } from './core/interceptors/httperror.interceptor';
import { DevToolsInterceptor } from './core/interceptors/dev-tools.interceptor';
import { RedirectComponent } from './redirect.component';
import localeEn from '@angular/common/locales/en';
import { registerLocaleData } from '@angular/common';
import {StaticPageComponent} from './static-page/static-page.component';
import { MaintenancePageComponent } from './maintenance-page/maintenance-page.component';

registerLocaleData(localeEn, 'en');

declare global {
  interface Window {
    analytics: any;
    ga: any;
    gtag: any;
    FS: any;
    Intercom: any;
    intercom_app_id: any;
    Upscope: any;
  }
}


@NgModule({ declarations: [
        AppComponent,
        RedirectComponent,
        StaticPageComponent,
        MaintenancePageComponent
    ],
    bootstrap: [AppComponent], imports: [BrowserModule,
        SharedModule,
        AuthModule,
        CoreModule,
        AppRoutingModule,
        BrowserAnimationsModule,
        ToastrModule.forRoot({
            timeOut: 10000
        }),
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: HttpLoaderFactory,
                deps: [HttpClient]
            }
        })], providers: [
        { provide: APP_ID, useValue: 'serverApp' },
        AuthService,
        InitService,
        {
            provide: APP_INITIALIZER,
            useFactory: (is: InitService) => function () {
                return is.load();
            },
            deps: [InitService],
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: HttpTokenInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: HttpErrorInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: DevToolsInterceptor,
            multi: true
        },
        provideHttpClient(withInterceptorsFromDi(), withFetch())
    ] })
export class AppModule {
}

/**
 * The `.v2` in the suffix is a CACHE-BREAK LEVER, not a schema version.
 *
 * `src/server.ts` used to serve everything under /browser with `maxAge: '1y'`,
 * so `/assets/i18n/en.json` went out with max-age=31536000. Every browser that
 * loaded the site before that was fixed is holding a copy it will not
 * revalidate for up to a year — and redeploying cannot fix a request the
 * browser never makes again. Renaming the files changes the URL, which is the
 * only thing those clients will act on.
 *
 * It should not need bumping again: assets now go out `no-cache`, so ordinary
 * copy changes reach everyone on their next load (see
 * core/utils/static-cache-control.ts). Bump it only if a caching layer we do
 * not control pins these files again — and rename the files in
 * src/assets/i18n/ to match, since the loader builds `prefix + lang + suffix`.
 *
 * Prefix is spelled out because passing a suffix means passing both.
 */
// required for AOT compilation
export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, '/assets/i18n/', '.v2.json');
}
